import { SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import Form from 'src/components/FormComponents/Form'
import * as S from './ReplacementForm.styles'
import { ReplacementDetail } from '../ReplacementDetail/ReplacementDetail'
import { ReplacementTable } from '../ReplacementTable/ReplacementTable'
import { actions } from '../../StockMovementsState'
import { BatchAndStockBatchDue, ReplacementSchemaType } from '../../StockMovementsTypes'
import { Button } from '@mui/material'
import { ReplacementConfirmation } from '../ReplacementConfirmation/ReplacementConfirmation'
import { useEffect, useState } from 'react'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { toast } from 'react-toastify'
import { useSelector } from 'src/utils/store'
import { AddressLabel } from '../AddressLabel'
import { formatDateUTC } from 'src/utils/formatDate'

export const ReplacementForm: React.FC = () => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [disabledConfirmButton, setDisabledConfirmButton] = useState(false)
  const stockMovements = useSelector((state) => state.stockMovements)
  const {
    inputAddress, selectedBatchAndStockBatchDue, badge, loading, batchAndStockBatchDueList, fromAddress, toAddress,
  } = stockMovements
  const { fc } = useFulfillmentCenter()

  const {
    handleSubmit,
    register,
    formState: { errors, touchedFields, isValid },
    getValues,
    trigger,
    setFocus,
    setError,
    setValue,
    clearErrors,
  } = useForm<ReplacementSchemaType>()
  const dispatch = useDispatch()

  const clearState = () => {
    dispatch(actions.clearState())
    setValue('badge', '')
    setValue('withdrawAddress', '')
    setValue('batch', '')
    setValue('inputAddress', '')
    setValue('quantity', '')
    clearErrors()
  }

  const onSubmit: SubmitHandler<ReplacementSchemaType> = () => {
    setShowConfirmation(true)
  }

  const onCancelConfirmation = () => {
    setShowConfirmation(false)
  }

  const onSubmitConfirm = () => {
    setDisabledConfirmButton(true)
    const onSuccess = () => {
      clearState()
      setShowConfirmation(false)
      setDisabledConfirmButton(false)
    }

    if (!inputAddress) {
      toast.error('O endereço é obrigatório!')
      return
    }

    if (!selectedBatchAndStockBatchDue) {
      toast.error('O lote é obrigatório!')
      return
    }

    if (!badge) {
      toast.error('O crachá do supervisor é obrigatório!')
      return
    }

    dispatch(actions.moveBatch({
      addressId: inputAddress.idLocalizacao,
      lotStockId: selectedBatchAndStockBatchDue.stockBatchDue.idEstoqueValidade,
      qty: Number(getValues('quantity')),
      userBadge: badge,
      onSuccess,
      setDisabledConfirmButton,
    }))
  }

  const onBadgeSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('badge')

    const onSuccess = () => {
      input.blur()
      setFocus('withdrawAddress')
    }

    if (validationSuccess) {
      dispatch(
        actions.validateSupervisorBadge({
          badge: getValues('badge'),
          onSuccess,
          setError: (message) => setError('badge', { message }),
        }),
      )
    }
  }

  const onWithdrawAddressSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('withdrawAddress')
    const onSuccess = () => {
      dispatch(actions.updateMovementsField({ key: 'fromAddress', value: getValues('withdrawAddress') }))
      input.blur()
      setFocus('batch')
    }

    if (validationSuccess) {
      dispatch(
        actions.getAddress({
          fcId: fc.id,
          address: getValues('withdrawAddress'),
          onSuccess,
          setError: (message) => setError('withdrawAddress', { message }),
        }),
      )
    }
  }

  const onBatchSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('batch')

    const onSuccess = () => {
      input.blur()
      setFocus('inputAddress')
    }

    if (validationSuccess) {
      dispatch(
        actions.getBatch({
          batchOrProductCode: getValues('batch'),
          address: getValues('withdrawAddress'),
          badge: badge || '',
          onSuccess,
          setError: (message) => setError('batch', { message }),
        }),
      )
    }
  }

  const onInputAddressSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('inputAddress')
    if (validationSuccess) {
      const onSuccess = () => {
        dispatch(actions.updateMovementsField({ key: 'toAddress', value: getValues('inputAddress') }))
        input.blur()
        setFocus('quantity')
      }

      dispatch(actions.getAddress({
        fcId: fc.id,
        address: getValues('inputAddress'),
        onSuccess,
        setError: (message) => setError('inputAddress', { message }),
      }))
    }
  }

  const onQuantitySubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('quantity')
    if (validationSuccess) {
      input.blur()
    }
  }

  const fieldSubmitActions: Record<keyof ReplacementSchemaType, (input: HTMLInputElement) => void> = {
    badge: onBadgeSubmit,
    withdrawAddress: onWithdrawAddressSubmit,
    batch: onBatchSubmit,
    inputAddress: onInputAddressSubmit,
    quantity: onQuantitySubmit,
  }

  const onPressEnter = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (loading) return

    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement
      fieldSubmitActions[input.name](input)
    }
  }

  const onSelectBatch = (batch: BatchAndStockBatchDue) => {
    dispatch(actions.setBatchAndStockBatchDueSelected(batch))
  }

  const resolveAddressLabel = () => {
    const address = getValues('inputAddress')
    return address && <AddressLabel address={address} />
  }

  useEffect(() => {
    setFocus('inputAddress')
  }, [selectedBatchAndStockBatchDue, setFocus])

  useEffect(() => {
    if (!badge) {
      setFocus('badge')
    }
  }, [badge, setFocus])

  useEffect(() => {
    dispatch(actions.clearState())
  }, [dispatch])

  return (
    <S.Form as={Form} onSubmit={handleSubmit(onSubmit)}>
      {showConfirmation && (
        <ReplacementConfirmation
          movimentations={[{
            description: selectedBatchAndStockBatchDue?.batch.descricao!,
            expirationDate: formatDateUTC(selectedBatchAndStockBatchDue?.batch.dataValidade!, 'DD/MM/YYYY'),
            inputAddress: getValues('inputAddress'),
            quantity: getValues('quantity') as number,
          }]}
          onCancel={onCancelConfirmation}
          onConfirm={onSubmitConfirm}
          disabledConfirmButton={disabledConfirmButton}
        />
      )}
      <S.CustomFieldMui
        onKeyPress={onPressEnter}
        inputProps={{ ...register('badge', { required: true, disabled: !!badge }) }}
        disabled={!!badge}
        error={!!errors.badge}
        helperText={errors.badge?.message}
        label='replacementForm.badgelabel'
        name='badge'
        testId='replacement-form-badge'
        type='password'
        size='medium'
        fullWidth
        autoComplete='off'
        autoFocus
      />
      <S.CustomFieldMui
        inputProps={{ ...register('withdrawAddress', { required: true }) }}
        onKeyPress={onPressEnter}
        disabled={!badge}
        error={!!errors.withdrawAddress}
        helperText={errors.withdrawAddress?.message}
        label='replacementForm.withdrawAddresslabel'
        name='withdrawAddress'
        size='medium'
        fullWidth
        testId='replacement-form-withdrawAddress'
      />
      <S.CustomFieldMui
        inputProps={{ ...register('batch', { required: true }) }}
        onKeyPress={onPressEnter}
        disabled={!touchedFields?.withdrawAddress || !!errors.withdrawAddress || !fromAddress}
        error={!!errors.batch}
        helperText={errors.batch?.message}
        label='replacementForm.batchlabel'
        name='batch'
        size='medium'
        fullWidth
        testId='replacement-form-batch'
      />
      {batchAndStockBatchDueList && !selectedBatchAndStockBatchDue && (
        <ReplacementTable
          listBatch={batchAndStockBatchDueList}
          barcode={getValues('batch')}
          onClick={onSelectBatch}
        />
      )}
      <S.CustomFieldMui
        inputProps={{ ...register('inputAddress', { required: true }) }}
        onKeyPress={onPressEnter}
        disabled={!selectedBatchAndStockBatchDue}
        error={!!errors.inputAddress}
        helperText={errors.inputAddress?.message}
        label='replacementForm.inputAddresslabel'
        placeholder='Bipe ou digite o endereço de entrada'
        name='inputAddress'
        size='medium'
        fullWidth
        testId='replacement-form-inputAddress'
      />
      <S.CustomFieldMui
        inputProps={{
          ...register('quantity', {
            required: true,
            min: 1,
            max: { value: selectedBatchAndStockBatchDue?.stockBatchDue?.quantidade ?? 100, message: 'Valor maior que o disponivel!' },
            onBlur: () => trigger(),
          }),
        }}
        onKeyPress={onPressEnter}
        disabled={!touchedFields?.inputAddress || !!errors?.inputAddress || !toAddress}
        error={!!errors.quantity}
        helperText={errors.quantity?.message}
        type='number'
        label='replacementForm.quantitylabel'
        placeholder='Digite a quantidade a ser movimentada'
        name='quantity'
        size='medium'
        fullWidth
        testId='replacement-form-quantity'
      />

      {selectedBatchAndStockBatchDue && (
        <ReplacementDetail
          description={selectedBatchAndStockBatchDue.batch.descricao}
          expirationDate={new Date(selectedBatchAndStockBatchDue.batch.dataValidade)}
          quantity={selectedBatchAndStockBatchDue.stockBatchDue.quantidade}
          movementedQuantity={getValues('quantity')as number}
        />
      )}

      {resolveAddressLabel()}

      <S.ButtonSection>
        <Button disabled={!isValid} variant='contained' color='success' onClick={handleSubmit(onSubmit)}>Movimentar</Button>
        <Button onClick={clearState} variant='contained'>Limpar</Button>
      </S.ButtonSection>
    </S.Form>
  )
}
